import React, {Component, FunctionComponent, MouseEvent, useEffect, useRef, useState} from "react";
import {Button, Dimmer, Form, Icon, Input, Loader, Message, Modal, Pagination, Segment, Table} from "semantic-ui-react";
import DeleteDialog from "../components/deleteDialog";
import {IItemDialogProps, IItemsListState, calcPagination, DialogType} from './globals';

interface IImage {
    id:             string,
    sha512:         string,
    app:            string,
    version:        number,
    subversion:     number,
    build:          number,
    personal:       null | number,
    description:    string,
    upload:         number
}

const newImage = (): IImage => ({
    id:             "",
    sha512:         "",
    app:            "",
    version:        0,
    subversion:     0,
    build:          0,
    personal:       null,
    description:    "",
    upload:         (new Date()).getTime()
});

const ImageEditDialog: FunctionComponent<IItemDialogProps<IImage>> = ({item, type, onClose}) => {
    const [data, setData] = useState(item);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [file, setFile] = useState("");

    const upload = useRef<HTMLInputElement>(null);

    const isValid = data.app.length && (typeof data.personal === "number" ? data.personal > -1 : true) && (type === "new" ? file.length : true);

    useEffect(() => {
        fetch("./customer")
            .then(res => res.ok
                ? res.json().then((json) => setCustomers(json.map((item: any) => ({text: item.name, value: item.id}))))
                : res.text().then(text => setError(text))
            )
    }, []);

    return (
        <Modal
            open
            closeIcon
            size="tiny"
            icon="edit"
            onClose={_ => onClose(false)}
        >
            <Modal.Header>{`${type === "new" ? "Create" : "Edit"} image`}</Modal.Header>
            <Modal.Content>
                <Dimmer active={loading} inverted>
                    <Loader>Process image info</Loader>
                </Dimmer>
                <Form>
                    <Form.Input error={!data.app.length} label="App" placeholder="app" value={data.app} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({...data, app: e.target.value})}/>
                    <Form.Group widths="equal">
                        <Form.Input type="number" min="0" fluid label="Version" placeholder="version" value={data.version} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({...data, version: e.target.valueAsNumber})}/>
                        <Form.Input type="number" min="0" fluid label="Subversion" placeholder="subversion" value={data.subversion} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({...data, subversion: e.target.valueAsNumber})}/>
                        <Form.Input type="number" min="0" fluid label="Build" placeholder="build" value={data.build} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setData({...data, build: e.target.valueAsNumber})}/>
                    </Form.Group>
                    <Form.TextArea label="Description" placeholder="Tell about image..." value={data.description} onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setData({...data, description: e.currentTarget.value})}/>
                    <Form.Checkbox label="Personal" checked={data.personal != null} onChange={(e: React.FormEvent<HTMLInputElement>) => setData({...data, personal: data.personal === null ? -1 : null})}/>
                    <Form.Select
                        label="Customer"
                        disabled={data.personal === null}
                        error={typeof data.personal === "number" ? data.personal === -1 : false}
                        value={typeof data.personal === "number" ? data.personal : -1}
                        options={customers}
                        onChange={(e: React.SyntheticEvent<HTMLElement>, eventData) => setData({...data, personal: typeof eventData.value === "number" ? eventData.value : -1})}/>
                    <Form.Input readOnly label="Upload" value={new Date(data.upload).toLocaleString()} />
                    { type === "new" ? <Form.Group>
                        <input
                            type='file'
                            style={{display: 'none'}}
                            ref={upload}
                            onChange={_ => {
                                if (!(upload.current && upload.current.files && upload.current.files[0]))
                                    return;

                                setFile(upload.current.value);

                                const formData = new FormData();
                                formData.append('name', upload.current.files[0].name);
                                formData.append('file', upload.current.files[0]);

                                fetch("/image/check", {
                                    method: "POST",
                                    body: formData
                                })
                                    .then(result => result.ok
                                        ? result.json().then(json => setData({...data, ...json}))
                                        : result.text().then(error => setError(error)))
                                    .catch(error => setError(error))
                            }}
                        />
                        <Form.Input width={11} readOnly error={file.length === 0} placeholder="Please select EFI File" value={file}/>
                        <Form.Button icon="upload" content="Upload EFI" onClick={(e: React.MouseEvent<HTMLButtonElement>) => upload.current!.click()}/>
                    </Form.Group> : null}
                </Form>
                <Message negative hidden={error.length === 0}>
                    {error}
                </Message>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    positive
                    disabled={!isValid}
                    icon="save"
                    labelPosition="left"
                    content="Save"
                    onClick={(e: React.MouseEvent) => {
                        setLoading(true);

                        const init = type === "new"
                            ? {
                                method: "POST",
                                body: (_ => {
                                    const formData = new FormData();

                                    formData.append('data', JSON.stringify(data));
                                    if (upload.current && upload.current.files)
                                        formData.append('file', upload.current.files[0]);

                                    return formData;
                                })()
                            }
                            : {
                                method: "PUT",
                                headers: {'content-type' : 'application/json'},
                                body: JSON.stringify(data)
                            };

                        fetch("/image", init)
                            .then(result => result.ok
                                ? onClose(true)
                                : result.text()
                                    .then(error => setError(error)))
                            .catch(error => setError(error))
                            .finally(() => setLoading(false))
                    }}
                />
            </Modal.Actions>
        </Modal>
    )
}

export default class Images extends Component<{}, IItemsListState<IImage>> {
    state = {
        loading: false,
        items: [],
        page: 1,
        filter: "",
        order: "upload",
        desc: true,
        dialog: null
    }

    componentDidMount() {
        this.updateImages();
    }

    updateImages() {
        this.setState({loading: true});
        fetch("./image")
            .then(res => res.ok
                ? res.json().then((json) => this.setState({items: json}))
                : res.text().then(text => alert(text))
            )
            .finally(() => this.setState({loading: false}))
    }

    render() {
        if (this.state.loading)
            return (
                <Dimmer active inverted>
                    <Loader size='massive'>Loading</Loader>
                </Dimmer>
            );

        const pagination = calcPagination<IImage>(this.state, 6, key => this.setState({page: key}));

        return (
            <Segment basic textAlign='right'>
                {this.state.dialog}

                <Button
                    floated='left'
                    icon="disk"
                    content="Add Image"
                    onClick={(e: MouseEvent) => this.setState({dialog: <ImageEditDialog
                            item={newImage()}
                            type={DialogType.new}
                            onClose={(update:boolean) => {
                                this.setState({dialog: null})
                                update && this.updateImages();
                            }}
                        />
                    })}
                />

                <Input
                    floated='right'
                    icon={{ name: 'search', circular: true, link: true }}
                    placeholder='Search...'
                    onChange={(e, data) => this.setState({filter: data.value})}
                />

                <Table celled striped selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>App</Table.HeaderCell>
                            <Table.HeaderCell>Version</Table.HeaderCell>
                            <Table.HeaderCell>Description</Table.HeaderCell>
                            <Table.HeaderCell>Personal</Table.HeaderCell>
                            <Table.HeaderCell>Upload</Table.HeaderCell>
                            <Table.HeaderCell>Action</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {pagination.data.map((image: IImage, idx) =>  <Table.Row
                            key={idx}
                            className='customer-row'
                            onClick={(e: MouseEvent) => this.setState({dialog: <ImageEditDialog
                                    item={image}
                                    type={DialogType.edit}
                                    onClose={(update: boolean) => {
                                        this.setState({dialog: null})
                                        update && this.updateImages();
                                    }}
                                />
                            })}
                        >
                            <Table.Cell>{image.app}</Table.Cell>
                            <Table.Cell>{`${image.version}.${image.subversion}.${image.build}`}</Table.Cell>
                            <Table.Cell>{image.description}</Table.Cell>
                            <Table.Cell collapsing textAlign="center">{<Icon name={image.personal === null ? "square outline" : "check square outline"}/>}</Table.Cell>
                            <Table.Cell collapsing>{new Date(image.upload).toLocaleString()}</Table.Cell>
                            <Table.Cell collapsing textAlign="center" className="customer-action-cell" onClick={(e: MouseEvent) => e.stopPropagation()}>
                                <Icon
                                    link
                                    name="download"
                                    onClick={(e: MouseEvent) => window.open(`/image/${image.id}`,'_self')}
                                />
                                <Icon
                                    link
                                    name="remove"
                                    onClick={(e: MouseEvent) => {
                                        e.stopPropagation();
                                        this.setState({dialog: <DeleteDialog
                                                id={image.id}
                                                label={`${image.app} ${image.version}.${image.subversion}.${image.build}`}
                                                uri="image"
                                                onClose={(update: boolean) => {
                                                    this.setState({dialog: null})
                                                    update && this.updateImages();
                                                }}
                                            />
                                        })
                                    }}
                                />
                            </Table.Cell>
                        </Table.Row>)}
                    </Table.Body>

                    {pagination.ctrl}

                </Table>
            </Segment>
        )
    }
}